import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { OPEN_COOKIE_SETTINGS, getConsent, setConsent, type ConsentChoice } from "@/lib/analytics";

/**
 * Cookie banner asking before Google Analytics is loaded. Shown on the first
 * visit, and again from the footer's "Cookie settings" link. Rendered only
 * after mount, since the choice lives in localStorage: the prerendered HTML
 * never contains it, so hydration isn't affected.
 */
export function CookieConsent() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setIsOpen(true);
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, open);
  }, []);

  if (!isOpen) return null;

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    setIsOpen(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-[20px] border border-border bg-background p-5 md:p-6 shadow-2xl flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className="flex gap-3 flex-1">
          <Cookie className="w-6 h-6 text-accent shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-foreground/80 leading-relaxed">
            We'd like to use Google Analytics cookies to understand how visitors use our website so
            we can improve it. They're only set if you accept. See our{" "}
            <Link href="/privacy-policy" className="font-semibold text-accent hover:underline underline-offset-4">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={() => choose("denied")}
            className="flex-1 md:flex-none rounded-full px-5 border-[#C9CFDA] text-[#0B1020] hover:border-[#0B1020]"
          >
            Decline
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => choose("granted")}
            className="flex-1 md:flex-none rounded-full px-5 bg-brand hover:bg-brand text-white"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
